import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { generatePDF } from '../../../redux/actions/file/file';
import { getElsLookups } from '../../../redux/actions/lookups/lookups';
import { getProfileDetails, resetGetProfileDetails } from '../../../redux/actions/profile/profileDetails';
import { setReadOnly } from '../../../redux/actions/readOnly/readOnly';
import { FileState } from '../../../redux/reducers/getFile/getFileReducer';
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer';
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { GeneratePDFPayload } from '../../../types/pdf/GeneratePDFPayload';
import CampaignList from '../../../components/CampaignList/CampaignList';
import Button from '../../../components/common/Button/Button';
import LoadingSpinner from '../../../components/common/LoadingSpinner/LoadingSpinner';
import LandingBanner from '../../../components/LandingBanner/LandingBanner';
import Footer from '../../../layouts/Footer/Footer';
import Header from '../../../layouts/Header/Header';
import { isCampaignListDisplayed } from '../../../utils/campaignList';
import { formatRoll } from '../../../utils/formatter';
import "./Overview.css";
import { selectELSLookups, selectGetFile, selectProfileGetDetails, selectProfileGetOverview } from '../../../redux/selectors/exportSelectorPaths';
import trackCustomGA4Event from '../../../utils/GA4EventHelpers/trackCustomGA4Event';

export default function Overview(): JSX.Element {

    const { t } = useTranslation(["transition", "campaign_list"]);
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const profileDetails: ProfileDetailsState = useSelector(selectProfileGetDetails);
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const fileState: FileState = useSelector(selectGetFile);
    const [downloading, setDownloading] = useState(false);
    const [isProfileDetailsReqSent, setIsProfileDetailsReqSent] = useState(false);
    const [reviewing, setReviewing] = useState(false)
    const rollNumber = sessionStorage.getItem("rollNumber") as string
    const [address, setAddress] = useState("")

    useEffect(() => {
        dispatch(resetGetProfileDetails())
    }, [dispatch]);

    useEffect(() => {
        if (!profileOverview.loading && profileOverview.profile) {
            setAddress(profileOverview.profile.profile_address)
        }
    }, [profileOverview])

    const reviewReturn = (e: React.MouseEvent) => {
        e.preventDefault();
        if (profileOverview.profile.current_campaign) {
            setIsProfileDetailsReqSent(true);
            dispatch(getProfileDetails(profileOverview.profile.current_campaign.currentSnapshotId));
        }

        trackCustomGA4Event('view_submission', {
            is_current_year: (String(process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR) === String(profileOverview.profile.current_campaign.year)) ? "Current Submission" : "Previous Submission",
        })
        setReviewing(true);
        dispatch(setReadOnly(true))

        if (!elsLookupsState.success && profileOverview.profile.profile_type_code !== "GEV") { // disable ELS calls for GEV portfolio
            const propertyCode = sessionStorage.getItem("propertyCode") ?? ""
            dispatch(getElsLookups(propertyCode));
        }
    }

    useEffect(() => {
        if (isProfileDetailsReqSent && !profileDetails.loading && reviewing) {
            // If profile type code is GEV, system doesn't check for ELS response success before navigating to portal
            if ((profileOverview.profile.profile_type_code === "GEV" || elsLookupsState.success) && profileDetails.success) {
                sessionStorage.removeItem('incomeReturnType'); // remove incomeReturnType for WIP as it's irrelevant
                sessionStorage.setItem("rollNumber", rollNumber)
                sessionStorage.setItem("profileId", String(profileOverview.profile.profile_id));
                sessionStorage.setItem("activeCampaignYear", String(profileOverview.profile.current_campaign.year));
                sessionStorage.setItem("campaignId", String(profileOverview.profile.current_campaign.campaignId));
                sessionStorage.setItem("campaignStatusCode", String(profileOverview.profile.current_campaign.statusCode));
                sessionStorage.setItem("currentSnapshotId", String(profileOverview.profile.current_campaign.currentSnapshotId));
                setReviewing(false);
                navigate('/portal')
            } else if (!profileDetails.success) {
                toast.error("Failed to retrieve profile details");
            }
        }
    }, [isProfileDetailsReqSent, profileDetails, elsLookupsState, reviewing, navigate, profileOverview, rollNumber]);

    const downloadPdf = (e: React.MouseEvent) => {
        e.preventDefault();
        const campaignYear = profileOverview.profile.current_campaign.year;
        const payload: GeneratePDFPayload = {
            language: i18n.language,
            rollNumber: sessionStorage.getItem("rollNumber") as string,
            profileId: profileOverview.profile.profile_id,
            campaignId: profileOverview.profile.current_campaign.campaignId,
            campaignYear: campaignYear,
            snapshotRequestType: "2",
            wac: sessionStorage.getItem("wac")
        }
        trackCustomGA4Event('download_submission', {
            is_current_year: (String(process.env.REACT_APP_ACTIVE_CAMPAIGN_YEAR) === String(campaignYear)) ? "Current Submission" : "Previous Submission",
        })
        dispatch(generatePDF(payload));
        setDownloading(true);
    }

    useEffect(() => {
        if (downloading && fileState.loading === false) {
            setDownloading(false);
            if (fileState.status === 200) {
                const url = fileState.s3url
                const downloadLink = document.createElement("a")
                downloadLink.href = url
                downloadLink.download = sessionStorage.getItem("rollNumber") + "_" + profileOverview.profile.current_campaign.year + "_AIRP.pdf";
                downloadLink.click()
            } else {
                toast.error(fileState.message);
            }
        }
    }, [downloading, fileState, profileOverview.profile.current_campaign.year]);

    return (<>
        <Header showAutoSave={false} isInternalUser={false} />
        <LandingBanner />
        <div className='continue-body-container'>
            <LoadingSpinner loading={
                profileDetails.loading || reviewing || profileOverview.loading
                || fileState.loading
            }>
                <div className='continue-body'>
                    <h1>{t("overview.thankyou")}</h1>
                    <p>{t("overview.submitted")}</p>
                    <div className='roll-info-container'>
                        <p className="strong roll-num-container">{formatRoll(rollNumber)}</p>
                        <p className="address-container">{address?.length > 260 ? address?.slice(0, 260) + "..." : address}</p>
                    </div>
                    <div className="transition-btn-group">
                        <Button type="submit"
                            onClick={e => downloadPdf(e)}
                            text={
                                <Trans t={t}
                                    i18nKey="download"
                                    ns="campaign_list"
                                    components={{ i: <i /> }}
                                />
                            } />
                        <div className='button-group'>
                            <Button onClick={e => reviewReturn(e)} type="secondary" text={t("overview.view-return")} />
                            <Button onClick={() => navigate('/landing')} type="secondary" text={t("return")} />
                        </div>
                    </div>
                    {
                        isCampaignListDisplayed(profileOverview) &&
                        <div className="campaign-list-wrapper">
                            <div className="campaign-list">
                                <CampaignList />
                            </div>
                        </div>
                    }
                </div>
            </LoadingSpinner>
        </div>
        <div className="start-footer">
            <Footer />
        </div>
    </>);
}
